import React, { useState, useEffect } from 'react';
import {
  ArrowRight, Check, Compass, Target, Lightbulb, MessageSquare, BarChart2,
  CheckCircle2, Award, TrendingUp, Settings, Play, ChevronRight, Users, 
  Flag, Scale, RefreshCw, BookOpen, Shield, Zap
} from 'lucide-react';

// Import the standalone components from main site
import HeaderNav from './HeaderNav';
import Contact from './Contact';
import FooterLan from './FooterLan';

// --- Color Palette ---
const COLORS = {
  primary: '#ce2223',       // Lusidea Red
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',  
  secondary: '#fdb52a',     // Accent Yellow/Gold
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb', 
  black: '#111827',         
  darkGray: '#374151',      
  mediumGray: '#6b7280',    
  lightGray: '#e5e7eb',     
  offWhite: '#f9fafb',      
  white: '#ffffff'
};

const SPACING = {
  xs: 'px-2 py-1',
  sm: 'px-4 py-2',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-16 md:py-20 lg:py-24',
  sectionMedium: 'py-12 md:py-16 lg:py-20',
  sectionCompact: 'py-8 md:py-12 lg:py-16',
  container: 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-8'
};

// --- Reusable Button Component ---
const Button = ({
  href,
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  icon: IconComponent,
  iconPosition = 'right',
  fullWidth = false,
  className = '',
  type = 'button',
  target,
  rel
}) => {
  const baseStyles = `
    inline-flex items-center justify-center gap-2 font-semibold
    transition-all duration-300 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    ${fullWidth ? 'w-full' : ''}
    whitespace-nowrap
  `;

  const sizeStyles = {
    xs: 'px-3 py-1.5 text-xs rounded-md',
    sm: 'px-4 py-2 text-sm rounded-md',
    md: 'px-5 py-2.5 text-base rounded-lg',
    lg: 'px-6 py-3 text-lg rounded-lg'
  };

  const variants = {
    primary: `bg-primary hover:bg-primaryDark text-white focus-visible:ring-primary shadow-md hover:shadow-lg active:shadow-sm`,
    secondary: `bg-secondary hover:bg-secondaryDark text-black focus-visible:ring-secondary shadow-md hover:shadow-lg active:shadow-sm`,
    outlinePrimary: `bg-transparent border-2 border-primary text-primary hover:bg-primaryLight focus-visible:ring-primary`,
    outlineGray: `bg-transparent border border-lightGray text-darkGray hover:border-mediumGray hover:text-black focus-visible:ring-mediumGray`,
    text: `bg-transparent text-primary hover:text-primaryDark hover:underline focus-visible:ring-primary`,
    dark: `bg-black hover:bg-black/80 text-white focus-visible:ring-black shadow-md hover:shadow-lg active:shadow-sm`
  };

  const iconElement = IconComponent ? <IconComponent size={size === 'lg' ? 20 : size === 'sm' ? 16 : 18} strokeWidth={2.5} /> : null;

  const content = (
    <>
      {iconPosition === 'left' && iconElement}
      {children}
      {iconPosition === 'right' && iconElement}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={`${baseStyles} ${sizeStyles[size]} ${variants[variant]} ${className}`}
        target={target}
        rel={rel}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseStyles} ${sizeStyles[size]} ${variants[variant]} ${className}`}
    >
      {content}
    </button>
  );
};

// --- Service Hero Section ---
const ServiceHero = () => {
  return (
    <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 md:pb-20 lg:pb-24 overflow-hidden bg-gradient-to-br from-primaryLight via-white to-secondaryLight/30">
      {/* Abstract shapes for visual interest */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"></div>
      
      <div className={`${SPACING.container} z-10 relative`}>
        <div className="flex flex-col lg:flex-row lg:gap-16 items-center">
          {/* Text Content Area */}
          <div className="lg:w-7/12 text-center lg:text-left">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center lg:justify-start text-sm mb-6 text-mediumGray">
              <a href="/" className="hover:text-primary transition-colors">Home</a>
              <ChevronRight className="w-4 h-4 mx-2" />
              <a href="/services" className="hover:text-primary transition-colors">Services</a>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-primary font-medium">Innovation Management</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-black leading-tight mb-6 md:mb-8">
              Innovation Management <span className="text-primary relative">
                Redefined
                <svg className="absolute -bottom-2 left-0 w-full h-2 text-primary" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M0,0 Q50,10 100,0" stroke="currentColor" strokeWidth="5" fill="none" />
                </svg>
              </span> for Business Growth
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-darkGray mb-8 md:mb-10 lg:mb-12 max-w-2xl mx-auto lg:mx-0">
              We specialize in offering Innovation Management as a Service (IMaaS), a comprehensive solution designed to streamline innovation processes, foster creative thinking, and transform ideas into measurable business value for organizations of all sizes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button href="#contact" variant="primary" size="lg" icon={ArrowRight} className="w-full sm:w-auto">
                Schedule a Consultation
              </Button>
              <Button href="#framework" variant="outlineGray" size="lg" icon={Play} iconPosition="left" className="w-full sm:w-auto">
                Watch Our Approach
              </Button>
            </div>

            {/* Key Benefits - badges with icons */}
            <div className="mt-10 md:mt-12 lg:mt-16 pt-6 md:pt-8 border-t border-lightGray/60">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/80 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-black">ISO 56000 Compliant</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/80 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center">
                    <Compass className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-black">Holistic Approach</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/80 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center">
                    <Settings className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-black">Proprietary Framework</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/80 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-primaryLight flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-black">Measurable Results</span>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Image Area */}
          <div className="lg:w-5/12 mt-12 sm:mt-16 lg:mt-0 w-full max-w-md mx-auto lg:max-w-none lg:mx-0">
            <div className="relative z-20">
              {/* Main image */}
              <div className="rounded-2xl overflow-hidden shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <img
                  src="/img/innovation-management.jpg"
                  alt="Team collaborating on innovation strategy"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              
              {/* Floating card element */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-4 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="flex items-center gap-3">
                  <Award size={24} className="text-primary" />
                  <div>
                    <div className="text-xs text-mediumGray font-medium">ISO Standard</div>
                    <div className="text-lg font-bold text-black">56000 Certified</div>
                  </div>
                </div>
              </div>
              
              {/* Second floating element */}
              <div className="absolute -top-4 -right-4 bg-secondary/90 text-black rounded-lg shadow-lg py-2 px-4 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="font-bold">IMaaS Platform</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Overview Section ---
const Overview = () => {
  return (
    <section className="py-16 md:py-20 lg:py-24 relative overflow-hidden" id="overview">
      {/* Background pattern */}
      <div className="absolute inset-0 -z-10 opacity-30" style={{backgroundImage: 'radial-gradient(#e5e7eb 1px, transparent 1px)', backgroundSize: '1rem 1rem'}} aria-hidden="true"></div>
      
      <div className={SPACING.container}>
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block rounded-full bg-primaryLight px-4 py-1 mb-3">
            <h2 className="text-sm font-semibold text-primary uppercase tracking-wide">
              Our Approach
            </h2>
          </div>
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-5">
            Lusidea's Strategic <span className="text-primary">Roadmap</span> to Innovation Success
          </h3>
        </div>

        {/* Content blocks with alternating layout */}
        <div className="space-y-20 md:space-y-24">
          {/* Block 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-4">
                Structured Approach to Innovation Excellence
              </h3>
              <p className="text-base sm:text-lg text-darkGray mb-6">
                Lusidea delivers a comprehensive innovation management system that helps organizations transform their approach to developing new ideas. We assess your current innovation capabilities, implement proven methodologies to enhance creativity and efficiency, and empower leadership teams to foster a culture where breakthrough thinking flourishes across all levels.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-primaryLight flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                    <Check size={14} className="text-primary" />
                  </div>
                  <p className="text-darkGray">Structured innovation processes aligned with business goals</p>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-primaryLight flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                    <Check size={14} className="text-primary" />
                  </div>
                  <p className="text-darkGray">Full organizational engagement from leadership to frontline</p>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-primaryLight flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                    <Check size={14} className="text-primary" />
                  </div>
                  <p className="text-darkGray">Culture transformation towards innovation mindset</p>
                </div>
              </div>
              <Button href="#framework" variant="outlinePrimary" size="md" icon={ArrowRight}>
                Explore Our Framework
              </Button>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="/img/systematic-approach.jpg" 
                    alt="Systematic innovation approach"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -bottom-5 -right-5 bg-white rounded-lg shadow-lg p-3 max-w-xs">
                  <div className="flex items-center gap-3">
                    <Zap size={20} className="text-secondary" />
                    <p className="text-sm font-medium">Transform ideas into measurable business value</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Block 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="/img/quality-standards.jpg" 
                    alt="Innovation quality standards"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -bottom-5 -left-5 bg-white rounded-lg shadow-lg p-3 max-w-xs">
                  <div className="flex items-center gap-3">
                    <Award size={20} className="text-primary" />
                    <p className="text-sm font-medium">ISO 56000 aligned innovation processes</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-4">
                Elevating Innovation Quality Standards
              </h3>
              <p className="text-base sm:text-lg text-darkGray mb-6">
                Our proprietary Innovation Management framework aligns with ISO 56000 international standards, enabling your organization to implement, measure, and maintain world-class innovation processes. This standardized approach ensures consistent quality in ideation, development, and implementation phases while providing a globally recognized benchmark for innovation excellence.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-primaryLight flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                    <Check size={14} className="text-primary" />
                  </div>
                  <p className="text-darkGray">Benchmark against international standards</p>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-primaryLight flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                    <Check size={14} className="text-primary" />
                  </div>
                  <p className="text-darkGray">Ongoing quality assessment and improvement</p>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-primaryLight flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                    <Check size={14} className="text-primary" />
                  </div>
                  <p className="text-darkGray">Recognized certification of innovation excellence</p>
                </div>
              </div>
              <Button href="#contact" variant="outlinePrimary" size="md" icon={ArrowRight}>
                Learn About Our Standards
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Framework Section (3 Steps) ---
const Framework = () => {
  const steps = [
    {
      id: "frame",
      title: "Frame",
      icon: Flag,
      color: "primary",
      description: "Building the Innovation Foundation",
      content: "At the Frame stage, we establish the foundation for successful innovation. We work with organizations to develop a clear innovation strategy that aligns with business objectives, design optimal team structures, and implement proven innovation processes. This crucial first phase creates a sturdy infrastructure built on ISO 56000 principles that supports all subsequent innovation activities.",
      features: [
        "Innovation strategy development",
        "Team structure and governance",
        "Innovation process design",
        "Capability assessment"
      ]
    },
    {
      id: "scale",
      title: "Scale",
      icon: Scale,
      color: "secondary",
      description: "Expanding Capabilities & Implementation",
      content: "The Scale phase focuses on expanding innovation capabilities from pilot initiatives to enterprise-wide systems. We implement specialized digital tools, develop key performance indicators to measure innovation success, and create effective pathways to bring innovations to market. This methodical approach transforms promising concepts into tangible business solutions with clearly defined ROI.",
      features: [
        "Innovation portfolio management",
        "Innovation metrics and KPIs",
        "Scaling successful pilots",
        "Innovation ecosystem development"
      ]
    },
    {
      id: "improve",
      title: "Improve",
      icon: RefreshCw,
      color: "primary",
      description: "Continuous Optimization & Growth",
      content: "The Improve stage establishes continuous innovation excellence through systematic refinement and adaptation. We implement data-driven feedback loops, optimize innovation processes based on performance analytics, and ensure your innovation capabilities evolve with changing market dynamics. This creates a self-sustaining innovation ecosystem that consistently delivers competitive advantage and long-term business growth.",
      features: [
        "Learning and feedback systems",
        "Process optimization",
        "Innovation maturity advancement",
        "Knowledge management"
      ]
    }
  ];

  const [activeStep, setActiveStep] = useState(0);

  return (
    <section id="framework" className="py-16 md:py-20 lg:py-24 bg-offWhite relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-white to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-64 bg-gradient-to-t from-white to-transparent"></div>
      
      <div className={SPACING.container + " relative z-10"}>
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block rounded-full bg-secondaryLight px-4 py-1 mb-3">
            <h2 className="text-sm font-semibold text-secondaryDark uppercase tracking-wide">
              Our Methodology
            </h2>
          </div>
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-4">
            Our 3-Step Framework: Transforming <span className="text-primary">Ideas</span> Into <span className="text-primary">Measurable Results</span>
          </h3>
          <p className="text-base sm:text-lg text-darkGray">
            Our ISO 56000-aligned methodology provides a structured approach that guides organizations from concept development to market implementation with measurable business impact.
          </p>
        </div>

        {/* Framework Steps - Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => setActiveStep(index)}
              className={`
                px-6 py-3 rounded-lg flex items-center gap-3 text-lg font-semibold transition-all duration-300
                ${activeStep === index
                  ? (step.color === 'primary' ? 'bg-primary text-white shadow-lg' : 'bg-secondary text-black shadow-lg')
                  : 'bg-white text-darkGray hover:bg-lightGray'}
              `}
            >
              {React.createElement(step.icon, { size: 20 })}
              {step.title}
            </button>
          ))}
        </div>

        {/* Active Step Content */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-10 lg:p-12 border border-lightGray">
          {steps.map((step, index) => (
            <div key={index} className={`${activeStep === index ? 'block' : 'hidden'}`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-full ${step.color === 'primary' ? 'bg-primaryLight' : 'bg-secondaryLight'} flex items-center justify-center`}>
                      {React.createElement(step.icon, { 
                        size: 28, 
                        className: step.color === 'primary' ? 'text-primary' : 'text-secondaryDark' 
                      })}
                    </div>
                    <div>
                      <h4 className="text-2xl font-bold text-black">{step.title}</h4>
                      <p className="text-mediumGray">{step.description}</p>
                    </div>
                  </div>
                  
                  <p className="text-base sm:text-lg text-darkGray mb-8">
                    {step.content}
                  </p>
                  
                  <div className="space-y-4 mb-8">
                    {step.features.map((feature, i) => (
                      <div key={i} className="flex items-start">
                        <div className={`w-6 h-6 rounded-full ${step.color === 'primary' ? 'bg-primaryLight' : 'bg-secondaryLight'} flex items-center justify-center mt-0.5 mr-3 flex-shrink-0`}>
                          <Check size={14} className={step.color === 'primary' ? 'text-primary' : 'text-secondaryDark'} />
                        </div>
                        <p className="text-darkGray">{feature}</p>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    href={`#${step.id}-details`} 
                    variant={step.color} 
                    size="md" 
                    icon={ArrowRight}
                  >
                    Learn More About {step.title}
                  </Button>
                </div>
                
                <div>
                  <div className="rounded-xl overflow-hidden shadow-lg">
                    <img 
                      src={`/img/${step.id}-stage.jpg`} 
                      alt={`${step.title} stage visualization`}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Step indicators for mobile - dot navigation */}
        <div className="flex justify-center gap-3 mt-8 lg:hidden">
          {steps.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveStep(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                activeStep === index 
                  ? 'bg-primary scale-125' 
                  : 'bg-lightGray hover:bg-mediumGray'
              }`}
              aria-label={`View step ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Stats Section ---
const Stats = () => {
  const statistics = [
    {
      percentage: "87%", 
      description: "executives believe innovation is essential to their organization's success and growth. (McKinsey)"
    },
    {
      percentage: "79%", 
      description: "of the top 20% of innovation companies have a well-defined innovation strategy. (PwC)"
    },
    {
      percentage: "64%", 
      description: "of global executives believe a lack of skills is an issue restricting their ability to innovate. (GE)"
    },
    {
      percentage: "41%", 
      description: "of marketers believe their biggest barrier is their inability to measure impact. (Gartner)"
    }
  ];

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-b from-white to-offWhite">
      <div className={SPACING.container}>
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block rounded-full bg-primaryLight px-4 py-1 mb-3">
            <h2 className="text-sm font-semibold text-primary uppercase tracking-wide">
              Why It Matters
            </h2>
          </div>
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-4">
            Why Innovation Management
          </h3>
          <p className="text-base sm:text-lg text-darkGray">
            Innovation management is a critical driver of competitive advantage in today's business landscape. These statistics highlight why implementing a structured innovation management system is essential for sustainable growth:
          </p>
        </div>

        {/* Stats grid with animated counters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {statistics.map((stat, index) => (
            <div 
              key={index} 
              className="bg-white rounded-xl shadow-lg p-6 border border-lightGray hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="text-4xl sm:text-5xl font-bold text-primary mb-3">
                {stat.percentage}
              </div>
              <p className="text-darkGray">{stat.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- CTA Section ---
const CallToAction = () => {
  return (
    <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primaryLight to-secondaryLight/30 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 -z-10 w-full h-full overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-70"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-secondary/5 rounded-full blur-3xl opacity-70"></div>
      </div>
      
      <div className={SPACING.container}>
        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10 md:p-12 lg:p-16 border border-lightGray">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-6">
                Transform Your Organization's Innovation Capabilities
              </h2>
              <p className="text-base sm:text-lg text-darkGray mb-8">
                Don't let innovation potential remain untapped. Lusidea's expert innovation management service provides the strategic framework, implementation tools, and cultural transformation needed to create sustainable business growth. Our ISO 56000-compliant methodology ensures your organization develops innovation as a core competency that drives measurable results.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button href="#contact" variant="primary" size="lg" icon={MessageSquare} iconPosition="left">
                  Schedule a Consultation
                </Button>
                <Button href="#spark-hub" variant="secondary" size="lg" icon={Zap} iconPosition="left">
                  Try Our Innovation Platform
                </Button>
              </div>
            </div>
            
            <div>
              <div className="bg-offWhite rounded-xl p-6 sm:p-8">
                <h3 className="text-xl font-bold text-black mb-4 flex items-center gap-2">
                  <BookOpen size={20} className="text-primary" />
                  Innovation Insights Newsletter
                </h3>
                <p className="text-darkGray mb-6">
                  Subscribe to our monthly innovation digest for exclusive research, case studies, and actionable strategies to enhance your organization's innovation capabilities. Stay informed about emerging trends and best practices in innovation management.
                </p>
                
                {/* Newsletter signup */}
                <div className="bg-white rounded-lg p-5 shadow-md">
                  <h4 className="font-semibold text-black mb-3">Get Expert Innovation Insights Monthly</h4>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input 
                      type="email" 
                      placeholder="Your email address" 
                      className="flex-grow px-4 py-2 rounded-lg border border-lightGray focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                    />
                    <button className="px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primaryDark transition-colors">
                      Subscribe
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Main Page Component ---
const InnovationManagementPage = () => {
  return (
    <div className="font-sans antialiased text-darkGray bg-white overflow-x-hidden">
      <HeaderNav />
      
      <main className="space-y-8 md:space-y-0">
        <ServiceHero />
        <Overview />
        <Framework />
        <Stats />
        <CallToAction />
        <Contact />
      </main>
      
      <FooterLan />
    </div>
  );
};

export default InnovationManagementPage;